/**
 * Security Service
 * Handles HTTPS enforcement, CSP, and other security measures
 */

export class SecurityService {
  private static instance: SecurityService | null = null;

  static getInstance(): SecurityService {
    if (!SecurityService.instance) {
      SecurityService.instance = new SecurityService();
    }
    return SecurityService.instance;
  }

  /**
   * Initialize all security measures
   */
  initialize(): void {
    this.enforceHTTPS();
    this.setupContentSecurityPolicy();
    this.setupSecurityHeaders();
    this.preventClickjacking();
    this.disableConsoleInProduction();
    
    console.log('🔒 Security measures initialized');
  }

  /**
   * Enforce HTTPS in production
   */
  private enforceHTTPS(): void {
    // Only enforce in production
    if (process.env.NODE_ENV !== 'production') {
      console.log('🔓 HTTPS enforcement disabled in development');
      return;
    }

    // Check if already on HTTPS
    if (window.location.protocol === 'https:') {
      console.log('✅ Already on HTTPS');
      return;
    }

    // Redirect to HTTPS
    console.warn('⚠️ Redirecting to HTTPS...');
    const httpsUrl = 'https:' + window.location.href.substring(window.location.protocol.length);
    window.location.replace(httpsUrl);
  }

  /**
   * Setup Content Security Policy
   */
  private setupContentSecurityPolicy(): void {
    // CSP is best set via meta tag in HTML or HTTP headers
    // This is a runtime check to ensure it's configured
    
    const metaCSP = document.querySelector('meta[http-equiv="Content-Security-Policy"]');
    
    if (!metaCSP && process.env.NODE_ENV === 'production') {
      console.warn('⚠️ Content Security Policy not configured in HTML');
      console.warn('Add CSP meta tag to public/index.html for better security');
    } else {
      console.log('✅ Content Security Policy configured');
    }
  }

  /**
   * Setup security headers (client-side validation)
   */
  private setupSecurityHeaders(): void {
    // These headers should be set by the server/CDN
    // This method validates they are present
    
    if (process.env.NODE_ENV === 'production') {
      // Check for HSTS header (can't be set client-side)
      console.log('ℹ️ Ensure server sets these headers:');
      console.log('  - Strict-Transport-Security');
      console.log('  - X-Content-Type-Options: nosniff');
      console.log('  - X-Frame-Options: DENY');
      console.log('  - X-XSS-Protection: 1; mode=block');
      console.log('  - Referrer-Policy: strict-origin-when-cross-origin');
    }
  }

  /**
   * Prevent clickjacking attacks
   */
  private preventClickjacking(): void {
    // Prevent the page from being embedded in an iframe
    if (window.self !== window.top) {
      console.error('🚨 Clickjacking attempt detected!');
      
      // Break out of iframe
      window.top!.location = window.self.location;
    }
  }

  /**
   * Disable console in production
   */
  private disableConsoleInProduction(): void {
    if (process.env.NODE_ENV === 'production') {
      // Disable console methods to prevent information leakage
      const noop = () => {};
      
      console.log = noop;
      console.debug = noop;
      console.info = noop;
      console.warn = noop;
      // Keep console.error for critical issues
    }
  }

  /**
   * Check if connection is secure
   */
  isSecureConnection(): boolean {
    return window.location.protocol === 'https:' || 
           window.location.hostname === 'localhost' ||
           window.location.hostname === '127.0.0.1';
  }

  /**
   * Validate secure context
   */
  isSecureContext(): boolean {
    return window.isSecureContext;
  }

  /**
   * Get security status
   */
  getSecurityStatus(): {
    isHTTPS: boolean;
    isSecureContext: boolean;
    protocol: string;
    hostname: string;
    environment: string;
  } {
    return {
      isHTTPS: window.location.protocol === 'https:',
      isSecureContext: window.isSecureContext,
      protocol: window.location.protocol,
      hostname: window.location.hostname,
      environment: process.env.NODE_ENV || 'development'
    };
  }

  /**
   * Sanitize user input to prevent XSS
   */
  sanitizeInput(input: string): string {
    const div = document.createElement('div');
    div.textContent = input;
    return div.innerHTML;
  }

  /**
   * Validate URL to prevent open redirect
   */
  isValidRedirectUrl(url: string): boolean {
    try {
      const urlObj = new URL(url, window.location.origin);
      
      // Only allow same-origin redirects
      return urlObj.origin === window.location.origin;
    } catch {
      return false;
    }
  }

  /**
   * Generate secure random string
   */
  generateSecureRandom(length: number = 32): string {
    const array = new Uint8Array(length);
    window.crypto.getRandomValues(array);
    return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
  }

  /**
   * Hash data using SHA-256
   */
  async hashData(data: string): Promise<string> {
    const encoder = new TextEncoder();
    const dataBuffer = encoder.encode(data);
    const hashBuffer = await window.crypto.subtle.digest('SHA-256', dataBuffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(byte => byte.toString(16).padStart(2, '0')).join('');
  }
}

export default SecurityService;
